import React, { useMemo, useState } from 'react';
import { useAuth } from '../../auth/AuthProvider';
import { readLinkProfileSession } from '../auth/session';
import { formatOnTimeRoleLabel } from '../utils/access';
import { assignDriver, flagOrderIssue, updateOrderStage } from '../lib/actions';
import { formatDateTime, formatMoney, whatsappHref } from '../lib/formatters';
import { buildStoreHandoffMessage, copyStoreHandoffMessage, getStoreWhatsAppHref } from '../lib/storeHandoff';
import StatusPill from './StatusPill';
import ProofPreview from './ProofPreview';
import { useOnTimeOrderEvents } from '../hooks/useOnTimeOrderEvents';

function actorFrom(user, claims) {
  const profileSession = readLinkProfileSession();
  const activeClaims = profileSession?.claims || claims || {};
  const name = String(profileSession?.profileName || activeClaims.displayName || activeClaims.fullName || activeClaims.name || user?.displayName || user?.email || 'Link user').trim();
  const role = activeClaims.onTimeRole || activeClaims.role || claims?.role || 'user';
  const roleLabel = activeClaims.onTimeRoleLabel || activeClaims.roleLabel || formatOnTimeRoleLabel(role);
  return { userId: user?.uid || activeClaims.uid || '', email: user?.email || activeClaims.email || '', name, role, roleLabel, profileId: profileSession?.profileId || activeClaims.profileId || '' };
}

function senderPatch(actor = {}) {
  return {
    sentByName: actor.name || 'Link user',
    sentByRole: actor.role || 'user',
    sentByRoleLabel: actor.roleLabel || formatOnTimeRoleLabel(actor.role || 'user'),
    senderName: actor.name || 'Link user',
    senderRole: actor.role || 'user',
    senderRoleLabel: actor.roleLabel || formatOnTimeRoleLabel(actor.role || 'user'),
  };
}

function buildCustomerMessage(order) {
  return `Hi ${order.customerName}, your order ${order.orderNumber || order.id} is currently ${String(order.stage || '').replaceAll('_', ' ')}. We will keep you updated.`;
}

export default function OrderDetailPanel({ order, drivers = [], onAction }) {
  const { claims, user } = useAuth();
  const timeline = useOnTimeOrderEvents(order?.id, order?.timeline || []);
  const timelineCount = Array.isArray(timeline) ? timeline.length : 0;
  const actor = useMemo(() => actorFrom(user, claims), [claims, user]);
  const [copied, setCopied] = useState(false);
  const [openPanel, setOpenPanel] = useState('summary');

  if (!order) {
    return <div className="link-detail-empty">Select an order to view command details.</div>;
  }

  const customerHref = whatsappHref(order.customerPhone, buildCustomerMessage(order));
  const storeHref = getStoreWhatsAppHref(order, order?.raw?.storeWhatsAppNumber || order?.raw?.storeWhatsappNumber || order?.raw?.storePhone || '');
  const storePreview = buildStoreHandoffMessage(order);

  async function handleCopyStoreCard() {
    const ok = await copyStoreHandoffMessage(order);
    if (!ok) return;
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1800);
  }

  async function handleSendToStore() {
    if (onAction) {
      await onAction('send_to_store', order);
    } else if (claims?.orgId) {
      await updateOrderStage({ orgId: claims.orgId, orderId: order.id, stage: 'awaiting_store', actor, patch: { 'timings.sentToStoreAt': new Date(), ...senderPatch(actor) }, message: 'Order sent to store from Control Tower' });
    }
    window.open(storeHref, '_blank', 'noopener,noreferrer');
  }

  const commandButtons = [
    { key: 'store', label: 'Send store', onClick: handleSendToStore },
    { key: 'copy', label: copied ? 'Copied' : 'Copy handoff', onClick: handleCopyStoreCard },
    { key: 'customer', label: 'WhatsApp customer', href: customerHref },
    { key: 'ready', label: 'Mark ready', onClick: () => (onAction ? onAction('ready_for_driver', order) : updateOrderStage({ orgId: claims?.orgId, orderId: order.id, stage: 'ready_for_driver', actor })) },
    { key: 'driver', label: 'Auto assign', onClick: () => {
      if (onAction) return onAction('assign_driver', order);
      const next = drivers.find((driver) => driver.status === 'available' || driver.load === 0) || drivers[0];
      if (!next) return null;
      return assignDriver({ orgId: claims?.orgId, orderId: order.id, driverId: next.id, driverName: next.name || next.fullName || next.id, actor });
    } },
    { key: 'issue', label: 'Flag issue', danger: true, onClick: () => (onAction ? onAction('flag_issue', order) : flagOrderIssue({ orgId: claims?.orgId, orderId: order.id, actor, note: 'Issue flagged from detail panel' })) },
  ];

  return (
    <div className="link-order-command-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="link-kicker">Order command</div>
          <h2 className="mt-1 text-2xl font-black text-white">{order.orderNumber || order.id}</h2>
          <p className="mt-1 text-sm text-slate-400">{order.source} · {order.timePlaced}</p>
        </div>
        <StatusPill stage={order.stage} />
      </div>

      {order.delay?.isDelayed ? (
        <div className={`link-detail-delay-strip ${order.delay.severity === 'critical' ? 'critical' : 'warning'}`}>
          <span>{order.delay.label}</span>
          <strong>{order.delay.durationLabel}</strong>
        </div>
      ) : null}

      <div className="link-command-tabs">
        {[
          ['summary', 'Summary'],
          ['handoff', 'Handoff'],
          ['timeline', `Timeline${timelineCount ? ` (${timelineCount})` : ''}`],
        ].map(([key, label]) => (
          <button key={key} type="button" onClick={() => setOpenPanel(key)} className={openPanel === key ? 'active' : ''}>{label}</button>
        ))}
      </div>

      {openPanel === 'summary' ? (
        <div className="link-detail-grid">
          <section>
            <div className="link-detail-label">Customer</div>
            <div className="mt-2 font-bold text-white">{order.customerName}</div>
            <div className="text-sm text-slate-300">{order.customerPhone || 'No phone saved'}</div>
            <div className="mt-2 text-sm text-slate-400">{order.address}</div>
            <div className="mt-1 text-sm text-slate-500">{order.notes || 'No notes yet.'}</div>
          </section>
          <section>
            <div className="link-detail-label">Ownership</div>
            <div className="mt-3 grid grid-cols-2 gap-3 text-sm">
              <span>Store <b>{order.store || '—'}</b></span>
              <span>ETA <b>{order.eta || '—'}</b></span>
              <span>Online <b>{order.assignedOnline || '—'}</b></span>
              <span>Driver <b>{order.assignedDriver || 'Unassigned'}</b></span>
              <span>Payment <b>{order.paymentMethod || '—'}</b></span>
              <span>Total <b>{formatMoney(order.total)}</b></span>
            </div>
          </section>
          <section className="md:col-span-2">
            <div className="link-detail-label">Items</div>
            <div className="mt-3 grid gap-2">
              {(order.items || []).map((item) => (
                <div key={`${item.name}-${item.qty}`} className="link-item-row">
                  <span>{item.name} × {item.qty}</span>
                  <span>{item.status}</span>
                </div>
              ))}
            </div>
          </section>
        </div>
      ) : null}

      {openPanel === 'handoff' ? (
        <section className="link-detail-section">
          <div className="link-detail-label">Store handoff preview</div>
          <pre className="link-handoff-preview">{storePreview}</pre>
          <div className="mt-3 flex flex-wrap gap-2">
            <a href={storeHref} target="_blank" rel="noreferrer" className="link-subtle-command">Open store WhatsApp</a>
            <button type="button" onClick={handleCopyStoreCard} className="link-subtle-command">{copied ? 'Copied' : 'Copy message'}</button>
          </div>
        </section>
      ) : null}

      {openPanel === 'timeline' ? (
        <section className="link-detail-section">
          <div className="flex items-start justify-between gap-3">
            <div>
              <div className="link-detail-label">Operational replay</div>
              <p className="mt-1 text-sm text-slate-400">Every major order action is stored as an auditable event.</p>
              {order.delay?.isDelayed ? (
                <p className="mt-2 text-sm text-amber-100">Current stage delay: {order.delay.label} · {order.delay.durationLabel}</p>
              ) : null}
            </div>
            <div className="rounded-full border border-cyan-300/20 bg-cyan-300/10 px-3 py-1 text-xs font-bold text-cyan-100">
              {timelineCount} events
            </div>
          </div>
          <div className="mt-4 space-y-3">
            {(timeline || []).map((event) => (
              <div key={event.id || `${event.at}-${event.text}`} className="link-timeline-row">
                <span />
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="text-white">{event.title || event.text}</div>
                    {event.durationFromPrevious ? (
                      <span className="rounded-full border border-slate-500/30 px-2 py-0.5 text-[11px] text-slate-400">
                        +{event.durationFromPrevious}
                      </span>
                    ) : null}
                  </div>
                  {event.text && event.text !== event.title ? <div className="mt-1 text-sm text-slate-300">{event.text}</div> : null}
                  <div className="mt-1 text-xs text-slate-500">
                    {event.at || formatDateTime(order.createdAt)}{event.actorLabel ? ` · ${event.actorLabel}` : ''}
                  </div>
                  {event.photoUrl ? <div className="mt-1 text-xs text-cyan-300">Proof attached</div> : null}
                </div>
              </div>
            ))}
            {!timelineCount ? <div className="text-sm text-slate-500">No timeline events recorded yet.</div> : null}
          </div>
        </section>
      ) : null}

      <div className="link-command-actions">
        {commandButtons.map((button) => button.href ? (
          <a key={button.key} href={button.href} target="_blank" rel="noreferrer" className="link-subtle-command">{button.label}</a>
        ) : (
          <button key={button.key} type="button" onClick={button.onClick} className={button.danger ? 'link-subtle-command link-danger-command' : 'link-subtle-command'}>{button.label}</button>
        ))}
      </div>

      <ProofPreview proof={order.proof} />
    </div>
  );
}
